import React, { useCallback, useContext } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'

import { useIsMobileScreen } from 'hooks/useScreenSize'
import PreviewableModal from '../../../../Tripkit/PreviewableModal/PreviewableModal'
import DesktopOfferMapCard from './DesktopOfferMapCard'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalFooter from 'components/Luxkit/Modal/ModalFooter'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import LineMapMarkerIcon from 'components/Luxkit/Icons/line/LineMapMarkerIcon'
import LineGlobeIcon from 'components/Luxkit/Icons/line/LineGlobeIcon'
import Clickable from 'components/Common/Clickable'
import BodyText from 'components/Luxkit/Typography/BodyText'
import LargeContentToggle from 'components/Common/LargeContentToggle'
import CardCarousel from 'components/Luxkit/Carousel/CardCarousel'
import Image from 'components/Common/Image'
import Group from 'components/utils/Group'
import PriceRowPrice from 'components/Luxkit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'components/Luxkit/PricePoints/PriceRowPriceCaption'
import PriceRowValueDiscountWithCaption from 'components/Luxkit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import { EXPERIENCE_MINIMUM_DISCOUNT_PERCENTAGE_TO_SHOW } from 'constants/experience'
import TextButton from 'components/Luxkit/Button/TextButton'
import { getExperienceOfferPageURL } from 'lib/offer/offerPageURL'
import useImmersiveModeHandlers from 'tripPlanner/hooks/useImmersiveModeHandlers'
import TripContext from 'tripPlanner/contexts/TripContext'
import ExperienceBookmarkToItineraryButton from 'tripPlanner/components/Bookmark/BookmarkButton/Experience/ExperienceBookmarkToItineraryButton'
import HeadingTextBlock from 'components/Luxkit/TextBlocks/HeadingTextBlock'
import { ExperienceBookmarkButton } from 'tripPlanner/components/Bookmark/BookmarkButton'
import MarkdownRender from 'components/Luxkit/MarkdownRender'
import { ExperienceItem } from 'tripPlanner/types/tripItem'
import { buildMyEscapesOrderLink, isLeBooking } from 'tripPlanner/utils'
import { BOOKING_DETAILS_COPY } from 'tripPlanner/config'
import SavedItemRemoveButton from 'tripPlanner/components/ItemCard/Common/SavedItemRemoveButton'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import BookedLabel from 'tripPlanner/components/BookedLabel'
import NotesAndAttachments from './NotesAndAttachments'
import Divider from 'components/Luxkit/Divider'

const SrcImage = styled.img`
  height: ${rem(120)};
  width: auto;
`

const CarouselContainer = styled.div`
  padding-bottom: ${rem(20)};
`

const Flex1 = styled.div`
  flex: 1;
`

interface Props {
  experience: App.ExperienceOffer
  tripItem?: ExperienceItem
  onClose: () => void
}

function LEExperienceSummaryCard({ experience, tripItem, onClose }: Props) {
  const isMobile = useIsMobileScreen()
  const { setImmersiveMode } = useImmersiveModeHandlers()
  const tripContext = useContext(TripContext)
  const tripId = tripContext?.tripId
  const canEdit = tripContext?.permissions.canEdit ?? false
  const viewAsTemplate = tripContext?.viewAsTemplate ?? false

  const onClickViewOffer = useCallback(() => {
    if (tripId) {
      setImmersiveMode({
        tripId,
      })
    }
  }, [tripId, setImmersiveMode])

  const carousel = <CardCarousel gap={8} gutterSize={20} desktopGutterSize={24} gutterStyle="overflow">
    {experience.images.map(image => (
      image.id ?
        <Image key={image.id} id={image.id} alt={image.title} height={120} /> :
        <SrcImage key={image.url} src={image.url} alt={image.title} />
    ))}
  </CardCarousel>

  const included = experience.copy.included?.trim()

  const bookingID = tripItem && isLeBooking(tripItem) && canEdit && !viewAsTemplate ? tripItem.confirmationCode : undefined

  const bookmarkButton = tripItem ? <SavedItemRemoveButton item={tripItem} /> : <ExperienceBookmarkButton offer={experience} iconOnly size="small" />

  const mainContent = <>
    <Group direction="horizontal" gap={8}>
      <Flex1>
        <VerticalSpacer gap={8}>
          {experience.location.name && <BodyTextBlock
            variant="medium"
            startIcon={<LineMapMarkerIcon />}
          >
            {experience.location.name}
          </BodyTextBlock>}
          {experience.vendor?.website && (
            <Clickable
              href={experience.vendor.website}
              target="_blank"
              rel="noopener noreferrer"
            >
              <BodyTextBlock
                variant="medium"
                startIcon={<LineGlobeIcon />}
                weight="bold"
                wrap="word-wrap"
              >
                {experience.vendor.website}
              </BodyTextBlock>
            </Clickable>
          )}
          {tripItem?.isBooked && <CSSBreakpoint max="mobile">
            <BookedLabel />
          </CSSBreakpoint>}
        </VerticalSpacer>
      </Flex1>
      <CSSBreakpoint max="mobile">
        {bookmarkButton}
      </CSSBreakpoint>
    </Group>

    <LargeContentToggle
      maxHeight={80}
      collapsedHeight={60}
      showMoreText="Read more"
      showLessText="Read less"
      showChevron={false}
    >
      <VerticalSpacer gap={12}>
        <MarkdownRender type="compact" content={experience.copy.dealDescription ?? experience.copy.description} fontSize="medium" />
        {included && <>
          <BodyText as="h6" variant="large" weight="bold">Inclusions</BodyText>
          <MarkdownRender type="compact" content={included} fontSize="medium" />
        </>}
      </VerticalSpacer>
    </LargeContentToggle>

    {tripItem && <>
      <Divider kind="primary" />
      <NotesAndAttachments item={tripItem} canEdit={canEdit} />
    </>}
  </>

  const footer = <ModalFooter>
    <Group direction="vertical" desktopDirection="horizontal" gap={12} horizontalAlign="space-between" verticalAlign="start" desktopVerticalAlign="center" wrap="wrap">
      {!tripItem?.isBooked && <Group direction="vertical" verticalAlign="end">
        <PriceRowPriceCaption>From</PriceRowPriceCaption>
        <PriceRowPrice
          size="L"
          price={experience.pricing.price}
          saleUnit={experience.pricing.saleUnit}
        />
        {experience.pricing.discountPercent >= EXPERIENCE_MINIMUM_DISCOUNT_PERCENTAGE_TO_SHOW && <PriceRowValueDiscountWithCaption
          size="L"
          originalValue={Math.ceil(experience.pricing.value)}
          discountPercentage={experience.pricing.discountPercent}
        />}
      </Group>}

      <Group direction="horizontal" horizontalAlign="center" gap={12} wrap="wrap">
        <TextButton kind="tertiary" size="large" to={getExperienceOfferPageURL(experience)} onClick={onClickViewOffer}>
          View offer
        </TextButton>
        {!bookingID && <ExperienceBookmarkToItineraryButton
          offer={experience}
          variant="button"
          dropdownPlacement="top"
        />}
        {!!bookingID && tripItem && <TextButton
          kind="secondary"
          size="large"
          to={buildMyEscapesOrderLink(tripItem)}
        >
          {BOOKING_DETAILS_COPY}
        </TextButton>}
      </Group>
    </Group>
  </ModalFooter>

  if (isMobile) {
    return (
      <PreviewableModal onRequestClose={onClose}>
        <ModalHeader onCloseButtonClick={onClose} title={experience.name} />
        <ModalBody>
          <ModalContent>
            <CarouselContainer>
              {carousel}
            </CarouselContainer>
            <VerticalSpacer gap={20}>
              {mainContent}
            </VerticalSpacer>
          </ModalContent>
        </ModalBody>
        {footer}
      </PreviewableModal>
    )
  }

  return (
    <DesktopOfferMapCard>
      <ModalBody>
        <ModalContent>
          <CarouselContainer>
            {carousel}
          </CarouselContainer>
          <VerticalSpacer gap={20}>
            <HeadingTextBlock
              variant="heading5"
              endIcon={<Group direction="horizontal" gap={12} verticalAlign="center">
                {tripItem?.isBooked && <BookedLabel />}
                {bookmarkButton}
              </Group>}
              fit="stretch"
            >
              {experience.name}
            </HeadingTextBlock>
            {mainContent}
          </VerticalSpacer>
        </ModalContent>
      </ModalBody>
      {footer}
    </DesktopOfferMapCard>
  )
}

export default LEExperienceSummaryCard
